import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import AOIChooser from '../../components/acquisition/AOIChooser';
import PeriodImageChooser from '../../components/acquisition/PeriodImageChooser';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import AttentionMessage from '../../components/core/AttentionMessage';
import Footer from '../../components/core/Footer';
import { Actions as Auth } from '../../../store/ducks/auth';
import { useStepNavigations, FIRST } from '../../../hooks/useStepNavigations';

/**
 * Module for the shore r process
 * @module ShoreR
 */

/**
 * @constant MODULE_ACCENT
 * @description ShoreR's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:ShoreR
 */
const MODULE_ACCENT = '#FF9D00';

const STEPS = [
  {
    label: 'forms.acquisition.1.title',
    content: 'forms.acquisition.1.description',
    /**
     * Wrapped rather than passed straight through: `SatelliteChooser` is shared by every module and
     * `makeRoutes` renders it with a fixed set of props, so the redesigned look is opted into here.
     */
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.acquisition.2.title',
    content: 'forms.acquisition.2.description',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    /**
     * `periodHints` rather than comparing this step's `component` against `PeriodImageChooser`:
     * the component is wrapped here to opt into the redesigned image selection, so the identity
     * check that used to decide whether to show the hints would no longer match.
     */
    label: 'forms.acquisition.periodAndImages.title',
    content: 'forms.acquisition.periodAndImages.description',
    requires: 'geometry',
    periodHints: true,
    component: (props) => <PeriodImageChooser {...props} variant="modern" />,
  },
];

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * ShoreR acquisition wizard.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:ShoreR
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  /**
   * The wizard header overlaps the card above it, so the indicator needs its own top room; the
   * max-width keeps the connectors short and centred instead of running the full card width.
   */
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
}));

/**
 * @function ShoreR
 * @description Main component for the ShoreR acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link AOIChooser} - Component for defining area of interest (AOI)
 * @see {@link PeriodImageChooser} - Component for selecting the period and refining the image list
 * **Called by:
 * @see {MainPage} - via the `/shore_r/:step?` Route.
 * @memberof module:ShoreR
 * @returns {JSX.Element} Complete acquisition page JSX element
 */
const ShoreR = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const { acquisitionData, navigate, navigateTo, stepAttendsRequirements, canRevisitStep, makeRoutes } = useStepNavigations(
    match,
    STEPS,
    '/main/processing_shore_r',
  );

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.acquisition.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={STEPS.map((el, i) => ({ id: String(i), label: t(el.label), disabled: !canRevisitStep(i) }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} variant="subtitle1" align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            {STEPS[step].periodHints && (
              <>
                <AttentionMessage
                  title={t('forms.shoreR.periodHint.title')}
                  message={t('forms.shoreR.periodHint.message')}
                />
                <AttentionMessage
                  title={t('forms.shoreR.timeSeriesHint.title')}
                  message={t('forms.shoreR.timeSeriesHint.message')}
                />
              </>
            )}
            <Switch>{makeRoutes(hasStep)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

export default ShoreR;
